import { useState } from 'react';
import { ArrowDownRight, ArrowUpRight, HelpCircle, Loader2 } from 'lucide-react';
import { Popover } from '@/components/ui/Popover';
import { Badge } from '@/components/ui/Badge';
import { useOverviewContributors } from '@/api/bi';
import type { ContributorMetric } from '@/api/bi';
import { compareRangeFor, useFilters, rangeISO } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

interface WhyKpiProps {
  metric: ContributorMetric;
  /** Format helper for the per-store contribution value. */
  format?: (n: number) => string;
  /** Tone for positive deltas (e.g. cancel rate flips). Defaults to true. */
  goodWhenUp?: boolean;
  className?: string;
}

const METRIC_LABELS: Record<ContributorMetric, string> = {
  gmv: 'GMV',
  orders_total: 'orders placed',
  orders_completed: 'completed orders',
  orders_cancelled: 'cancelled orders',
};

/**
 * "Why?" trigger that opens a small popover with the top contributing
 * stores + biggest movers for the given metric. Lazy-fetches data only
 * when first opened so we don't tax the dashboard on initial paint.
 */
export function WhyKpi({ metric, format, goodWhenUp = true, className }: WhyKpiProps) {
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const range = useFilters((s) => s.range);
  const compareMode = useFilters((s) => s.compareMode);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const storeId = useFilters((s) => s.storeId);
  const iso = rangeISO(range, timeRangeMode);
  const compareRange = compareRangeFor(range, compareMode, timeRangeMode);

  const fmtValue = format ?? ((n: number) => fmt.compact(n));

  const q = useOverviewContributors({
    start: iso.start,
    end: iso.end,
    compareStart: compareRange?.start,
    compareEnd: compareRange?.end,
    storeId,
    metric,
    limit: 5,
  });

  const handleOpenChange = (next: boolean) => {
    if (next && !hasOpened) setHasOpened(true);
    setOpen(next);
  };

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      side="bottom"
      align="end"
      className="w-80"
      trigger={
        <button
          type="button"
          aria-label={`Why did ${METRIC_LABELS[metric]} change?`}
          className={cn(
            'press inline-flex h-5 items-center gap-1 rounded-full border border-border bg-surface px-1.5 text-[10px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-ring',
            className,
          )}
        >
          <HelpCircle className="h-3 w-3" aria-hidden />
          why?
        </button>
      }
    >
      {!hasOpened ? null : q.isLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          Crunching contributors…
        </div>
      ) : q.isError ? (
        <p className="text-xs text-danger">Couldn't load contributors. Try again.</p>
      ) : !q.data?.contributors.length ? (
        <p className="text-xs text-muted-foreground">No store-level data in this window yet.</p>
      ) : (
        <WhyBody data={q.data} fmtValue={fmtValue} goodWhenUp={goodWhenUp} metric={metric} />
      )}
    </Popover>
  );
}

function WhyBody({
  data,
  fmtValue,
  goodWhenUp,
  metric,
}: {
  data: NonNullable<ReturnType<typeof useOverviewContributors>['data']>;
  fmtValue: (n: number) => string;
  goodWhenUp: boolean;
  metric: ContributorMetric;
}) {
  const { contributors, movers, totals } = data;
  return (
    <div className="space-y-3 text-xs">
      <header className="flex items-baseline justify-between">
        <p className="font-display text-sm font-semibold tracking-tight text-foreground">
          What moved {METRIC_LABELS[metric]}?
        </p>
        {totals.delta_pct != null ? (
          <Badge tone={tone(totals.delta_pct, goodWhenUp)}>
            {fmt.delta(totals.delta_pct, { unit: 'pct' })} vs prev
          </Badge>
        ) : null}
      </header>

      <section>
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Top contributors
        </p>
        <ul className="space-y-1.5">
          {contributors.map((c) => (
            <li key={c.store_id} className="flex items-center justify-between gap-3">
              <span className="truncate text-foreground" title={c.store_name}>
                {c.store_name}
              </span>
              <span className="flex items-center gap-2 text-muted-foreground">
                <span className="kpi-num text-foreground">{fmtValue(c.current)}</span>
                {c.share != null ? (
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] kpi-num">
                    {fmt.pct(c.share)}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {(movers.up.length > 0 || movers.down.length > 0) && (
        <section className="rounded-md border border-dashed border-border bg-surface px-2.5 py-2">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Biggest movers
          </p>
          <ul className="space-y-1">
            {movers.up.slice(0, 3).map((m) => (
              <Mover key={`up-${m.store_id}`} m={m} fmtValue={fmtValue} goodWhenUp={goodWhenUp} />
            ))}
            {movers.down.slice(0, 3).map((m) => (
              <Mover
                key={`down-${m.store_id}`}
                m={m}
                fmtValue={fmtValue}
                goodWhenUp={goodWhenUp}
              />
            ))}
          </ul>
        </section>
      )}

      <p className="text-[10px] text-muted-foreground">
        {totals.pareto_80_count} store{totals.pareto_80_count === 1 ? '' : 's'} drive 80% of total
        — {fmt.pct(totals.pareto_80_pct)} of the network.
      </p>
    </div>
  );
}

function Mover({
  m,
  fmtValue,
  goodWhenUp,
}: {
  m: { store_name: string; store_id: number; delta: number; delta_pct: number | null };
  fmtValue: (n: number) => string;
  goodWhenUp: boolean;
}) {
  const Icon = m.delta > 0 ? ArrowUpRight : ArrowDownRight;
  const color =
    tone(m.delta, goodWhenUp) === 'success'
      ? 'text-success'
      : tone(m.delta, goodWhenUp) === 'danger'
        ? 'text-danger'
        : 'text-muted-foreground';
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="truncate text-foreground" title={m.store_name}>
        {m.store_name}
      </span>
      <span className={cn('inline-flex items-center gap-1', color)}>
        <Icon className="h-3 w-3" aria-hidden />
        <span className="kpi-num">{fmtValue(Math.abs(m.delta))}</span>
        {m.delta_pct != null ? (
          <span className="kpi-num text-[10px] opacity-80">
            ({fmt.delta(m.delta_pct, { unit: 'pct' })})
          </span>
        ) : null}
      </span>
    </li>
  );
}

function tone(value: number | null, goodWhenUp: boolean): 'success' | 'danger' | 'neutral' {
  if (value == null || value === 0) return 'neutral';
  const positive = value > 0;
  const isGood = goodWhenUp ? positive : !positive;
  return isGood ? 'success' : 'danger';
}
